import { useState } from 'react'
import { Users, Search, Download, Upload, MoreVertical, Edit, Trash2, Mail, Tag, Megaphone, X } from 'lucide-react'
import {
  useClients,
  useDeleteClient,
  useBulkDeleteClients,
  useBulkUpdateClients,
  useExportCsv,
  useClientTags,
} from '@hooks/useClients'
import { useCampaigns, useAddClientsToCampaign } from '@hooks/useCampaigns'
import { useUIStore } from '@stores/ui'
import { Client } from '@/types'
import { formatDateTime } from '@lib/utils'
import { ClientFilters } from '@services/client.service'

interface ClientListProps {
  onEdit: (client: Client) => void
  onImportClick: () => void
}

export function ClientList({ onEdit, onImportClick }: ClientListProps) {
  const [filters, setFilters] = useState<ClientFilters>({
    page: 1,
    limit: 20,
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedClients, setSelectedClients] = useState<number[]>([])
  const [showCampaignModal, setShowCampaignModal] = useState(false)

  const { data, isLoading, error } = useClients(filters)
  const { data: availableTags } = useClientTags()
  const { data: campaignsData } = useCampaigns({ status: 'DRAFT', limit: 100 })
  const deleteMutation = useDeleteClient()
  const bulkDeleteMutation = useBulkDeleteClients()
  const bulkUpdateMutation = useBulkUpdateClients()
  const exportMutation = useExportCsv()
  const addToCampaignMutation = useAddClientsToCampaign()
  const showToast = useUIStore((state) => state.showToast)

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setFilters({ ...filters, search: searchTerm, page: 1 })
  }

  const handleStatusFilter = (status: Client['status'] | undefined) => {
    setFilters({ ...filters, status, page: 1 })
  }

  const handleTagFilter = (tag: string) => {
    const currentTags = filters.tags || []
    const newTags = currentTags.includes(tag)
      ? currentTags.filter((t) => t !== tag)
      : [...currentTags, tag]

    setFilters({ ...filters, tags: newTags.length > 0 ? newTags : undefined, page: 1 })
  }

  const handleDelete = async (id: number) => {
    if (confirm('Are you sure you want to delete this client?')) {
      await deleteMutation.mutateAsync(id)
    }
  }

  const handleBulkDelete = async () => {
    if (selectedClients.length === 0) return

    if (confirm(`Are you sure you want to delete ${selectedClients.length} client(s)?`)) {
      await bulkDeleteMutation.mutateAsync(selectedClients)
      setSelectedClients([])
    }
  }

  const handleBulkUpdateStatus = async (status: Client['status']) => {
    if (selectedClients.length === 0) return

    await bulkUpdateMutation.mutateAsync({
      clientIds: selectedClients,
      updates: { status },
    })
    setSelectedClients([])
  }

  const handleAddToCampaign = async (campaignId: number) => {
    if (selectedClients.length === 0) return

    try {
      const result = await addToCampaignMutation.mutateAsync({
        campaignId,
        clientIds: selectedClients,
      })

      if (result.skipped > 0 || result.alreadyInCampaign.length > 0) {
        showToast(`Added ${result.added} client(s) to campaign. ${result.skipped} skipped.`, 'warning')
      } else {
        showToast(`Successfully added ${result.added} client(s) to campaign.`, 'success')
      }

      setSelectedClients([])
      setShowCampaignModal(false)
    } catch {
      showToast('Failed to add clients to campaign', 'error')
    }
  }

  const handleExport = async () => {
    const blob = await exportMutation.mutateAsync(filters)
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `clients-${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  const toggleSelectAll = () => {
    if (selectedClients.length === clients.length) {
      setSelectedClients([])
    } else {
      setSelectedClients(clients.map((c) => c.id))
    }
  }

  const toggleSelectClient = (id: number) => {
    setSelectedClients((prev) =>
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id]
    )
  }

  if (isLoading) {
    return <LoadingSkeleton />
  }

  if (error) {
    return (
      <div className="rounded-xl border border-rose-500/20 bg-rose-500/10 p-6 text-center">
        <p className="text-rose-400">Failed to load clients</p>
      </div>
    )
  }

  const clients = data?.data || []
  const pagination = data?.pagination

  return (
    <div className="space-y-4">
      {/* Filters and Actions */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <form onSubmit={handleSearch} className="flex flex-1 gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by email, name..."
                className="input-modern pl-10"
              />
            </div>
            <button
              type="submit"
              className="btn-primary"
            >
              Search
            </button>
          </form>

          <div className="flex gap-2">
            <button
              onClick={onImportClick}
              className="btn-secondary"
            >
              <Upload size={18} />
              Import CSV
            </button>
            <button
              onClick={handleExport}
              disabled={exportMutation.isPending}
              className="btn-secondary disabled:opacity-50"
            >
              <Download size={18} />
              Export
            </button>
          </div>
        </div>

        {/* Status Filters */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleStatusFilter(undefined)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              !filters.status
                ? 'bg-cyan-500 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            All
          </button>
          <button
            onClick={() => handleStatusFilter('NEW')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.status === 'NEW'
                ? 'bg-blue-600 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            New
          </button>
          <button
            onClick={() => handleStatusFilter('IN_SEQUENCE')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.status === 'IN_SEQUENCE'
                ? 'bg-cyan-600 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            In Sequence
          </button>
          <button
            onClick={() => handleStatusFilter('CONTACTED')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.status === 'CONTACTED'
                ? 'bg-emerald-600 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            Contacted
          </button>
          <button
            onClick={() => handleStatusFilter('RESPONDED')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.status === 'RESPONDED'
                ? 'bg-purple-600 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            Responded
          </button>
          <button
            onClick={() => handleStatusFilter('UNSUBSCRIBED')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.status === 'UNSUBSCRIBED'
                ? 'bg-amber-600 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            Unsubscribed
          </button>
          <button
            onClick={() => handleStatusFilter('BOUNCED')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.status === 'BOUNCED'
                ? 'bg-orange-600 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            Bounced
          </button>
          <button
            onClick={() => handleStatusFilter('BLACKLISTED')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.status === 'BLACKLISTED'
                ? 'bg-rose-600 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            Blacklisted
          </button>
        </div>
        {/* Tag Filters */}
        {availableTags && availableTags.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <Tag size={18} className="text-slate-500" />
            {availableTags.map((tag) => (
              <button
                key={tag}
                onClick={() => handleTagFilter(tag)}
                className={`flex items-center gap-1 rounded-full px-3 py-1 text-sm transition-colors ${
                  filters.tags?.includes(tag)
                    ? 'bg-cyan-500 text-white'
                    : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {selectedClients.length > 0 && (
        <div className="flex items-center justify-between rounded-xl bg-cyan-500/10 border border-cyan-500/20 p-4">
          <p className="text-sm font-medium text-cyan-400">
            {selectedClients.length} client(s) selected
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setShowCampaignModal(true)}
              className="btn-primary text-xs px-3 py-1.5"
            >
              <Megaphone size={16} />
              Add to Campaign
            </button>
            <button
              onClick={() => handleBulkUpdateStatus('NEW')}
              className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs text-white hover:bg-emerald-700"
            >
              Mark New
            </button>
            <button
              onClick={() => handleBulkUpdateStatus('UNSUBSCRIBED')}
              className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs text-white hover:bg-amber-700"
            >
              Unsubscribe
            </button>
            <button
              onClick={() => handleBulkUpdateStatus('BLACKLISTED')}
              className="rounded-lg bg-slate-600 px-3 py-1.5 text-xs text-white hover:bg-slate-700"
            >
              Blacklist
            </button>
            <button
              onClick={handleBulkDelete}
              className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs text-white hover:bg-rose-700"
            >
              Delete
            </button>
            <button
              onClick={() => setSelectedClients([])}
              className="btn-secondary text-xs px-3 py-1.5"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Client Table */}
      {clients.length === 0 ? (
        <div className="flex flex-col items-center justify-center stat-card p-12">
          <Users className="mb-4 h-16 w-16 text-slate-600" />
          <h3 className="text-lg font-semibold text-slate-200">No clients found</h3>
          <p className="mt-2 text-sm text-slate-500">
            {filters.search || filters.status || filters.tags
              ? 'Try adjusting your filters'
              : 'Import your first clients to get started'}
          </p>
        </div>
      ) : (
        <div className="stat-card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-800/50 border-b border-slate-700/50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedClients.length === clients.length}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-slate-600 bg-slate-700 text-cyan-500 focus:ring-cyan-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                    Tags
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-slate-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {clients.map((client) => (
                  <ClientRow
                    key={client.id}
                    client={client}
                    isSelected={selectedClients.includes(client.id)}
                    onSelect={() => toggleSelectClient(client.id)}
                    onEdit={() => onEdit(client)}
                    onDelete={() => handleDelete(client.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-slate-700/50 px-6 py-4">
              <p className="text-sm text-slate-400">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} results
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setFilters({ ...filters, page: pagination.page - 1 })}
                  disabled={pagination.page === 1}
                  className="btn-secondary disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setFilters({ ...filters, page: pagination.page + 1 })}
                  disabled={pagination.page === pagination.totalPages}
                  className="btn-secondary disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Campaign Selection Modal */}
      {showCampaignModal && (
        <CampaignSelectionModal
          onClose={() => setShowCampaignModal(false)}
          onSelect={handleAddToCampaign}
          campaigns={campaignsData?.data || []}
          selectedCount={selectedClients.length}
          isLoading={addToCampaignMutation.isPending}
        />
      )}
    </div>
  )
}

interface CampaignSelectionModalProps {
  onClose: () => void
  onSelect: (campaignId: number) => void
  campaigns: Array<{ id: number; name: string; status: string }>
  selectedCount: number
  isLoading: boolean
}

function CampaignSelectionModal({
  onClose,
  onSelect,
  campaigns,
  selectedCount,
  isLoading,
}: CampaignSelectionModalProps) {
  const [selectedCampaign, setSelectedCampaign] = useState<number | null>(null)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-md stat-card">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-100">Add to Campaign</h3>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-700/50 hover:text-slate-200">
            <X size={20} />
          </button>
        </div>

        <p className="mb-4 text-sm text-slate-400">
          Select a campaign to add {selectedCount} client(s) to:
        </p>

        {campaigns.length === 0 ? (
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-6 text-center">
            <Megaphone className="mx-auto mb-2 h-8 w-8 text-slate-500" />
            <p className="text-sm text-slate-400">No draft campaigns available.</p>
            <p className="mt-1 text-xs text-slate-500">
              Create a new campaign first to add clients.
            </p>
          </div>
        ) : (
          <div className="mb-4 max-h-60 overflow-y-auto space-y-2">
            {campaigns.map((campaign) => (
              <label
                key={campaign.id}
                className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors ${
                  selectedCampaign === campaign.id
                    ? 'border-cyan-500/50 bg-cyan-500/10'
                    : 'border-slate-700/50 hover:border-slate-600'
                }`}
              >
                <input
                  type="radio"
                  name="campaign"
                  value={campaign.id}
                  checked={selectedCampaign === campaign.id}
                  onChange={() => setSelectedCampaign(campaign.id)}
                  className="h-4 w-4 border-slate-600 bg-slate-700 text-cyan-500 focus:ring-cyan-500"
                />
                <div className="flex-1">
                  <div className="font-medium text-slate-200">{campaign.name}</div>
                  <div className="text-xs text-slate-500">Status: {campaign.status}</div>
                </div>
              </label>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={() => selectedCampaign && onSelect(selectedCampaign)}
            disabled={!selectedCampaign || isLoading}
            className="btn-primary disabled:opacity-50"
          >
            {isLoading ? 'Adding...' : 'Add to Campaign'}
          </button>
        </div>
      </div>
    </div>
  )
}

interface ClientRowProps {
  client: Client
  isSelected: boolean
  onSelect: () => void
  onEdit: () => void
  onDelete: () => void
}

function ClientRow({ client, isSelected, onSelect, onEdit, onDelete }: ClientRowProps) {
  const [showMenu, setShowMenu] = useState(false)

  return (
    <tr className="hover:bg-slate-800/30 transition-colors">
      <td className="px-6 py-4">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onSelect}
          onClick={(e) => e.stopPropagation()}
          className="h-4 w-4 rounded border-slate-600 bg-slate-700 text-cyan-500 focus:ring-cyan-500"
        />
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center">
          <Mail className="mr-3 h-5 w-5 text-slate-500" />
          <div>
            {client.fullName && <div className="font-medium text-slate-100">{client.fullName}</div>}
            <div className={`text-sm ${client.fullName ? 'text-slate-500' : 'text-slate-200'}`}>
              {client.email}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <StatusBadge status={client.status} />
      </td>
      <td className="px-6 py-4">
        {client.tags && client.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {client.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="inline-flex rounded-full bg-cyan-500/15 px-2.5 py-0.5 text-xs font-medium text-cyan-400 border border-cyan-500/20"
              >
                {tag}
              </span>
            ))}
            {client.tags.length > 3 && (
              <span className="inline-flex items-center text-xs text-slate-500">
                +{client.tags.length - 3}
              </span>
            )}
          </div>
        ) : (
          <span className="text-sm text-slate-600">-</span>
        )}
      </td>
      <td className="px-6 py-4 text-sm text-slate-500">
        {formatDateTime(client.createdAt)}
      </td>
      <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
        <div className="relative inline-block">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-700/50 hover:text-slate-200"
          >
            <MoreVertical size={18} />
          </button>
          {showMenu && (
            <div className="absolute right-0 z-50 mt-2 w-48 rounded-xl bg-slate-800 border border-slate-700 shadow-xl shadow-black/20 overflow-hidden">
              <button
                onClick={() => {
                  onEdit()
                  setShowMenu(false)
                }}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-slate-700 hover:text-slate-100 transition-colors"
              >
                <Edit size={16} className="text-cyan-400" /> Edit Client
              </button>
              <div className="border-t border-slate-700">
                <button
                  onClick={() => {
                    onDelete()
                    setShowMenu(false)
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-rose-400 hover:bg-rose-500/10 transition-colors"
                >
                  <Trash2 size={16} /> Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </td>
    </tr>
  )
}

function StatusBadge({ status }: { status: Client['status'] }) {
  const statusConfig: Record<string, { color: string; label: string }> = {
    // New active states
    NEW: { color: 'bg-blue-500/15 text-blue-400 border border-blue-500/20', label: 'New' },
    IN_SEQUENCE: { color: 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/20', label: 'In Sequence' },
    CONTACTED: { color: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20', label: 'Contacted' },
    // Terminal states
    RESPONDED: { color: 'bg-purple-500/15 text-purple-400 border border-purple-500/20', label: 'Responded' },
    UNSUBSCRIBED: { color: 'bg-amber-500/15 text-amber-400 border border-amber-500/20', label: 'Unsubscribed' },
    BOUNCED: { color: 'bg-orange-500/15 text-orange-400 border border-orange-500/20', label: 'Bounced' },
    BLACKLISTED: { color: 'bg-rose-500/15 text-rose-400 border border-rose-500/20', label: 'Blacklisted' },
    // Legacy states (kept for backward compatibility)
    PENDING: { color: 'bg-blue-500/15 text-blue-400 border border-blue-500/20', label: 'Pending' },
    EMAIL_1_SENT: { color: 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/20', label: 'Email 1 Sent' },
    EMAIL_2_SENT: { color: 'bg-teal-500/15 text-teal-400 border border-teal-500/20', label: 'Email 2 Sent' },
    EMAIL_3_SENT: { color: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20', label: 'Email 3 Sent' },
    FAILED: { color: 'bg-rose-500/15 text-rose-400 border border-rose-500/20', label: 'Failed' },
  }

  const config = statusConfig[status] || { color: 'bg-slate-500/15 text-slate-400 border border-slate-500/20', label: status }

  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${config.color}`}>
      {config.label}
    </span>
  )
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {[...Array(10)].map((_, i) => (
        <div key={i} className="flex items-center gap-4 stat-card">
          <div className="h-12 w-12 skeleton rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-1/3 skeleton rounded" />
            <div className="h-3 w-1/4 skeleton rounded" />
          </div>
        </div>
      ))}
    </div>
  )
}
